/* eslint-disable @typescript-eslint/no-explicit-any */
import { Copy } from "lucide-react";

import { Button, Input, Select } from "../ds";
import { Row, Section, SegRow, ToggleRow, setIn } from "./FormatControls";
import type { SWidget } from "./model";
import { CURRENCY_OPTIONS, UNIT_OPTIONS, formatNumber, impliedCurrency, numberFormatOf, type NumberFormat } from "./numberFormat";

/* Format pane -> "Number format" (display.numberFormat, v2.4): Power BI's
 * Format / Display units / Decimal places, plus currency, negatives,
 * separator and prefix/suffix, with a live preview. Touching any control
 * turns the older one-word preset (display.format) into this setting. */

const DECIMALS = ["", "0", "1", "2", "3", "4"];
const TYPE_LABEL: Record<string, string> = { auto: "Automatic", number: "Number", currency: "Currency", percent: "Percent" };

/** Short summary shown on the collapsed section. */
function summaryOf(nf: NumberFormat): string {
  const parts = [TYPE_LABEL[nf.type ?? "auto"]];
  if (nf.type === "currency") parts.push(nf.currency || "USD");
  if (nf.type !== "percent" && nf.units && nf.units !== "auto") parts.push(nf.units === "none" ? "no units" : UNIT_OPTIONS.find((u) => u.value === nf.units)!.label.split(" ")[0]);
  if (nf.decimals != null) parts.push(`${nf.decimals} dp`);
  return parts.join(" · ");
}

export function NumberFormatSection({ widget, onChange, onApplyToPage }: { widget: SWidget; onChange: (patch: Partial<SWidget>, key?: string) => void; onApplyToPage?: (nf: NumberFormat) => void }) {
  const d = widget.display ?? {};
  const nf = numberFormatOf(d);
  const isSet = !!d.numberFormat || !!d.format;
  const type = nf.type ?? "auto";
  const knownCurrency = !nf.currency || CURRENCY_OPTIONS.some((c) => c.value === nf.currency);

  /** Write one numberFormat key; the older preset goes away. */
  const put = (k: keyof NumberFormat, v: any, live = false) => {
    let next = setIn(d, ["numberFormat"], { ...nf, [k]: v === "" ? undefined : v });
    if (!Object.keys(next.numberFormat ?? {}).length) next = setIn(next, ["numberFormat"], {});
    next = setIn(next, ["format"], undefined);
    onChange({ display: next }, live ? `fmt-${widget.id}-nf-${k}` : undefined);
  };
  const reset = () => onChange({ display: setIn(setIn(d, ["numberFormat"], undefined), ["format"], undefined) });

  const cur = impliedCurrency(widget);
  const samples = type === "percent" ? [0.2361, 0.05, -0.1234] : [252436118.5, 1234567, -48210.75];

  return (
    <Section id="numfmt" title="Number format" summary={isSet ? summaryOf(nf) : "Automatic"} canReset={isSet} onReset={reset}>
      <SegRow label="Format" value={type} onChange={(v) => put("type", v === "auto" ? undefined : v)} options={[{ value: "auto", label: "Auto", title: "Picked from the data" }, { value: "number", label: "Number" }, { value: "currency", label: "Currency" }, { value: "percent", label: "%" }]} />

      {type === "currency" ? (
        <Row label="Currency" stacked>
          <Select inputSize="sm" value={knownCurrency ? nf.currency ?? "USD" : "__other"} onChange={(e) => put("currency", e.target.value === "__other" ? "SR" : e.target.value)}>
            {CURRENCY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            <option value="__other">Other symbol…</option>
          </Select>
          {!knownCurrency ? <Input inputSize="sm" value={nf.currency ?? ""} maxLength={6} placeholder="e.g. SR" onChange={(e) => put("currency", e.target.value, true)} style={{ marginTop: 6 }} aria-label="Currency symbol" /> : null}
        </Row>
      ) : null}

      {type !== "percent" ? (
        <Row label="Display units" hint="Show 252,436,118 as 252.44M or 0.25B." stacked>
          <Select inputSize="sm" value={nf.units ?? "auto"} onChange={(e) => put("units", e.target.value === "auto" ? undefined : e.target.value)}>
            {UNIT_OPTIONS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
          </Select>
        </Row>
      ) : (
        <SegRow label="Values are stored as" value={nf.percentOf ?? "auto"} onChange={(v) => put("percentOf", v === "auto" ? undefined : v)} options={[{ value: "auto", label: "Auto" }, { value: "fraction", label: "0.25 = 25%" }, { value: "whole", label: "25 = 25%" }]} />
      )}

      <Row label="Decimal places" stacked>
        <Select inputSize="sm" value={nf.decimals == null ? "" : String(nf.decimals)} onChange={(e) => put("decimals", e.target.value === "" ? undefined : Number(e.target.value))}>
          {DECIMALS.map((v) => <option key={v} value={v}>{v === "" ? "Auto" : v}</option>)}
        </Select>
      </Row>

      <SegRow label="Negative numbers" value={nf.negative ?? "minus"} onChange={(v) => put("negative", v === "minus" ? undefined : v)} options={[{ value: "minus", label: "-1,234" }, { value: "parentheses", label: "(1,234)" }]} />
      <ToggleRow label="Thousands separator" hint="1,234,567 instead of 1234567." checked={nf.thousands !== false} onChange={(v) => put("thousands", v ? undefined : false)} />

      <div className="st-frow-pair">
        <Row label="Before the number" stacked>
          <Input inputSize="sm" value={nf.prefix ?? ""} maxLength={12} placeholder="e.g. ~" onChange={(e) => put("prefix", e.target.value, true)} />
        </Row>
        <Row label="After the number" stacked>
          <Input inputSize="sm" value={nf.suffix ?? ""} maxLength={12} placeholder="e.g.  /yr" onChange={(e) => put("suffix", e.target.value, true)} />
        </Row>
      </div>

      <div className="st-nfpreview" aria-live="polite">
        <div className="st-fsub">Preview</div>
        {samples.map((s) => (
          <div key={s} className="st-nfpreview__row">
            <span className="st-nfpreview__raw">{s.toLocaleString("en-US", { maximumFractionDigits: 4 })}</span>
            <span className="st-nfpreview__arrow">→</span>
            <span className="st-nfpreview__out">{formatNumber(nf, s, { impliedCurrency: cur })}</span>
          </div>
        ))}
      </div>

      {onApplyToPage ? (
        <Button size="sm" icon={Copy} onClick={() => onApplyToPage(nf)} style={{ alignSelf: "flex-start" }} title="Every chart, KPI and table on this page gets this number format (Ctrl+Z undoes it).">
          Use on every visual on this page
        </Button>
      ) : null}
    </Section>
  );
}

/** Data labels / value axis: their own display units and decimals
 * (Power BI lets both differ from the visual's number format). */
export function UnitsOverride({ widget, path, onChange, legacyFull }: { widget: SWidget; path: string[]; onChange: (patch: Partial<SWidget>, key?: string) => void; legacyFull?: boolean }) {
  const d = widget.display ?? {};
  let cfg: any = d;
  for (const k of path) cfg = cfg?.[k];
  cfg = cfg ?? {};
  const units: string = cfg.units ?? (legacyFull && cfg.format === "full" ? "none" : "");
  const put = (k: "units" | "decimals", v: any) => {
    let next = setIn(d, [...path, k], v);
    if (legacyFull) next = setIn(next, [...path, "format"], undefined);
    // the override lives on the new number format: carry the older preset over
    if (!next.numberFormat) next = setIn(next, ["numberFormat"], numberFormatOf(d));
    next = setIn(next, ["format"], undefined);
    onChange({ display: next });
  };
  const same = "As the visual";
  const nf = numberFormatOf(d);
  return (
    <div className="st-frow-pair">
      {nf.type !== "percent" ? (
        <Row label="Display units" stacked>
          <Select inputSize="sm" value={units} onChange={(e) => put("units", e.target.value || undefined)}>
            <option value="">{same}</option>
            {UNIT_OPTIONS.map((u) => <option key={u.value} value={u.value}>{u.label.split(" ")[0]}</option>)}
          </Select>
        </Row>
      ) : null}
      <Row label="Decimal places" stacked>
        <Select inputSize="sm" value={cfg.decimals == null ? "" : String(cfg.decimals)} onChange={(e) => put("decimals", e.target.value === "" ? undefined : Number(e.target.value))}>
          <option value="">{path.includes("yAxis") ? "Auto" : same}</option>
          {DECIMALS.slice(1).map((v) => <option key={v} value={v}>{v}</option>)}
        </Select>
      </Row>
    </div>
  );
}
